import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { Breadcrumb, DateRange, DateRangeDropdown, EmptyRow, formatCompact, formatCurrency, formatRoas, LoadingRow, MetricCard, presetToDates, readMetric, StatusBadge, TabsRow, useCxoResource } from "./shared";
import SimpleOverviewTab from "./tabs/simple-overview-tab";
import AdsetAdsTab from "./tabs/adset-ads-tab";
import AudienceTab from "./tabs/audience-tab";
import PlacementsTab from "./tabs/placements-tab";
import DevicesTab from "./tabs/devices-tab";

// Ad set detail — GET /cxo-dashboard/adsets/{id} for the header+cards, then
// one of five tabs (Overview/Ads/Audience/Placements/Devices).

const AdsetDetail: React.FC = () => {
  const { campaignId, adsetId } = useParams<{ campaignId: string; adsetId: string }>();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>("Last 7 Days");
  const { start_date, end_date } = useMemo(() => presetToDates(dateRange), [dateRange]);
  const baseParams = useMemo(() => ({ mode: "fixed", start_date, end_date }), [start_date, end_date]);
  const [tab, setTab] = useState("overview");

  const { data, loading } = useCxoResource<any>("/cxo-dashboard/adsets", adsetId, baseParams, !!adsetId);

  if (loading && !data) return <LoadingRow label="Loading ad set…" />;
  if (!data) return <EmptyRow label="Couldn't load this ad set." />;

  const adset = data.adset || {};
  const name = adset.name ?? data.name;
  const status = adset.status ?? data.status;
  const audienceSummary = adset.audience?.summary ?? data.audience?.summary;
  const campaignName = data.campaign?.name;
  const s = data.summary || {};

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "ads", label: `Ads${data.total != null ? ` (${data.total})` : ""}` },
    { id: "audience", label: "Audience" },
    { id: "placements", label: "Placements" },
    { id: "devices", label: "Devices" },
  ];

  return (
    <div className="space-y-5">
      <Breadcrumb
        trail={[
          { label: "Campaigns", to: "/cxo/campaigns" },
          { label: campaignName || "Campaign", to: campaignId ? `/cxo/campaigns/${campaignId}` : undefined },
          { label: name || "Ad set" },
        ]}
      />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{name}</h1>
            {status && <StatusBadge status={status} />}
          </div>
          {audienceSummary && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{audienceSummary}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DateRangeDropdown value={dateRange} onChange={setDateRange} />
          <button type="button" className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">
            <MoreHorizontal size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <MetricCard label="Spend" metric={readMetric(s.spend)} format={formatCurrency} invert empty="₦0.00" />
        <MetricCard label="Impressions" metric={readMetric(s.impressions)} format={formatCompact} empty="0" />
        <MetricCard label="Reach" metric={readMetric(s.reach)} format={formatCompact} empty="0" />
        <MetricCard label="Results" metric={readMetric(s.results)} format={formatCompact} empty="0" />
        <MetricCard label="Cost per result" metric={readMetric(s.cost_per_result)} format={formatCurrency} invert empty="₦0.00" />
        <MetricCard label="Reported value" metric={readMetric(s.reported_value ?? s.attributed_revenue)} format={formatCurrency} empty="₦0.00" />
        <MetricCard label="ROAS" metric={readMetric(s.roas)} format={formatRoas} empty="N/A" />
      </div>

      <TabsRow tabs={tabs} active={tab} onChange={setTab} />

      {adsetId && campaignId && (
        <>
          {tab === "overview" && <SimpleOverviewTab basePath="/cxo-dashboard/adsets" entityId={adsetId} baseParams={baseParams} />}
          {tab === "ads" && <AdsetAdsTab campaignId={campaignId} adsetId={adsetId} baseParams={baseParams} onOpenAd={(adId) => navigate(`/cxo/campaigns/${campaignId}/adsets/${adsetId}/ads/${adId}`)} />}
          {tab === "audience" && <AudienceTab basePath="/cxo-dashboard/adsets" entityId={adsetId} baseParams={baseParams} />}
          {tab === "placements" && <PlacementsTab basePath="/cxo-dashboard/adsets" entityId={adsetId} baseParams={baseParams} exportPath={`/cxo-dashboard/adsets/${adsetId}/placements/export`} />}
          {tab === "devices" && <DevicesTab basePath="/cxo-dashboard/adsets" entityId={adsetId} baseParams={baseParams} />}
        </>
      )}
    </div>
  );
};

export default AdsetDetail;
